export enum OrderStatus {
  CANCELED = 'CANCELED',
  DENIED = 'DENIED',
  FAILED = 'FAILED',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  AWAITING_SAMPLE = 'AWAITING_SAMPLE',
  ANALYZING = 'ANALYZING',
  REFERRAL_LAB_LD_REVIEW = 'REFERRAL_LAB_LD_REVIEW',
  CLINICIAN_REVIEW = 'CLINICIAN_REVIEW',
  COMPLETED = 'COMPLETED',
  REPORTED = 'REPORTED',
}

/** API `report_type` on requisition orders (patient portal). */
export enum ReportType {
  FAMILY_VARIANT_TESTING = 'FAMILY_VARIANT_TESTING',
  MONOGENIC = 'MONOGENIC',
  PGX = 'PGX',
  PRS = 'PRS',
  NDD_CNA = 'NDD_CNA',
  RARE_DISEASE = 'RARE_DISEASE',
  DATA_ONLY = 'DATA_ONLY',
  CLINICAL_RISK_ONLY = 'CLINICAL_RISK_ONLY',
  BASELINE_RISK = 'BASELINE_RISK',
  // APOE genotyping. Risk is a diplotype category, not a variant classification.
  EMERGING_GENES = 'EMERGING_GENES',
}

/** Maps to `OrderType.SINGLE_GENE` in results UI. */
export const REPORT_TYPES_SINGLE_GENE: readonly ReportType[] = [
  ReportType.MONOGENIC,
  ReportType.FAMILY_VARIANT_TESTING,
  ReportType.NDD_CNA,
  ReportType.RARE_DISEASE,
];

/** Maps to `OrderType.RISK_SCORE` (polygenic / clinical risk) in results UI. */
export const REPORT_TYPES_RISK_SCORE: readonly ReportType[] = [
  ReportType.PRS,
  ReportType.BASELINE_RISK,
  ReportType.CLINICAL_RISK_ONLY,
];

export enum OrderResultFinding {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  AVERAGE_RISK = 'AVERAGE_RISK',
  ELEVATED_RISK = 'ELEVATED_RISK',
  MINIMAL_IMPACT = 'MINIMAL_IMPACT',
  ALTERED_DOSE = 'ALTERED_DOSE',
  MAJOR_IMPACT = 'MAJOR_IMPACT',
  CONTRAINDICATED = 'CONTRAINDICATED',
  HIGH_RISK = 'HIGH_RISK',
  INTERMEDIATE_RISK = 'INTERMEDIATE_RISK',
  BORDERLINE_RISK = 'BORDERLINE_RISK',
  LOW_RISK = 'LOW_RISK',
  NOT_ANALYZED = 'NOT_ANALYZED',
  INCREASED_RISK = 'INCREASED_RISK',
  NOT_INCREASED_RISK = 'NOT_INCREASED_RISK',
  // APOE diplotype risk relative to e3/e3. NOT_REPORTABLE means the sample was
  // genotyped but sits outside the e2/e3/e4 set; NOT_ANALYZED means QC failed.
  DECREASED_RISK = 'DECREASED_RISK',
  NOT_REPORTABLE = 'NOT_REPORTABLE',
}

export interface OrderResult {
  description: string;
  uri: string;
  mimetype: string;
}

export interface ToplineResult {
  finding: OrderResultFinding;
  text: string;
  details: string;
}

export interface Order {
  created_at: string;
  order_id: string;
  product_id: string;
  product_name: string;
  /** Disease or test name, without the platform prefix. */
  product_test_name?: string | null;
  /** Platform and tier, for example "Atlas Cancer Plus". */
  product_family_name?: string | null;
  status: OrderStatus;
  report_type?: ReportType;
  /** Public product logo SVG URL. Omitted when the product has no logo. */
  logo_url?: string;
  results?: ToplineResult[];
  reported_at?: string;
  canceled_at?: string;
}

export interface ExtendedOrder extends Order {
  isReported: boolean;
}

export interface OrderStatusDetail {
  status_reason: 'QC_FAILURE_DATA' | 'QC_FAILURE_SEX_MISMATCH';
  status_message: string;
}

export interface ClassificationGene {
  primary_symbol: string;
  alternate_symbols: string[];
}

export interface ClassificationVariation {
  display: string;
  hgvs_g?: string;
  hgvs_p?: string;
  hgvs_t?: string;
  event?: string;
  summary?: string;
}

export interface OrderResultClassification {
  classification: string;
  zygosity: string;
  variation: ClassificationVariation;
  gene?: ClassificationGene;
  disease?: string;
  evidence?: string;
  inheritance?: string;
  // FAMILY_VARIANT_TESTING reports use a flatter shape with no nested `gene`.
  location?: string;
  gene_info?: string;
  interpretation_description?: string;
  variant_evidence?: string;
  familial_variant_detected?: string;
}

export interface ClinicalRiskInputs {
  age?: number;
  antihypertension_medication_use?: boolean;
  body_mass_index?: number;
  diagnosed_with_type_2_diabetes?: boolean;
  diastolic_blood_pressure?: number;
  fasting_glucose_level?: number;
  first_degree_family_with_type_2_diabetes?: boolean;
  first_or_second_degree_family_with_prostate_cancer?: string;
  hdl_cholesterol?: number;
  race?: string | null;
  sample?: string;
  sex?: string;
  smoking_status?: string;
  systolic_blood_pressure?: number;
  total_cholesterol?: number;
  triglyceride_level?: number;
  waist_circumference?: number;
}

export interface OrderDetailResult {
  classifications: OrderResultClassification[];
  clinical_summary?: string;
  clinical_risk_inputs?: ClinicalRiskInputs;
  disease_information?: string[];
  next_steps: string[];
  toplines: ToplineResult[];
}

export interface OrderDetailTest {
  approved_for_reporting: boolean;
  description: string;
  disclaimers: string[];
  genes: string[];
  limitations: string[];
  methods: string[];
  name: string;
  productversion_id: string;
}

export interface OrderDetailResultJSON {
  report_id: string;
  results: OrderDetailResult;
  tests: OrderDetailTest[];
}

export interface OrderDetail {
  order_id: string;
  requisition_id: string;
  product_id: string;
  product_name: string;
  status: OrderStatus;
  results: OrderResult[];
  submitted_at?: string;
  updated_at?: string;
  result_json: OrderDetailResultJSON;
  status_detail: OrderStatusDetail;
}
